import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';

import { map, range } from 'lodash';

import styled, { withTheme, css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import {
  ChevronLeft,
  ChevronRight,
  DoubleChevronLeft,
  DoubleChevronRight,
} from '@origo_vl_banking/icons';

const Paging = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
  padding-top: 20px;


  @media print {
    display: none;
  }
`;

const ButtonText = styled.span`
  font-weight: ${props => (props.current ? '700' : '400')};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const PagerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media print {
    display: none;
  }
`;

const Button = styled.button`
  border: 1px solid ${props => props.theme.borderColor};
  line-height: 100%;
  padding: 10px;
  cursor: pointer;
  height: 32px;
  width: 32px;
  outline: none;
  background-color: ${props =>
    (props.isArrow ? props.theme.iconColor : props.theme.formBackground)};
  /* background-color: 'white'; */

  ${({ noPagerBackground, isArrow, theme }) => {
    if (noPagerBackground) {
      return css`
        background-color: transparent;
      `;
    } else if (isArrow) {
      return css`
        background-color: ${theme.iconColor};
      `;
    }

    return css`
      background-color: ${theme.formBackground};
    `;
  }}

  color: ${props => (props.current ? 'white' : props.theme.textColor)};
  ${(props) => {
    if (props.current) {
      return `
        background-color: ${props.theme.iconColor};
`;
    }

    return '';
  }};
  ${(props) => {
    if (props.hide) {
      return 'visibility: hidden;';
    }
    return '';
  }} ${(props) => {
  if (props.isFirst && props.current) {
    return `
        border-radius: ${props.theme.borderRadius || '0px'} 0 0 ${props.theme.borderRadius ||
      '0px'};
    `;
  } else if (props.isLast && props.current) {
    return `
        border-radius: 0 ${props.theme.borderRadius || '0px'} ${props.theme.borderRadius ||
      '0px'} 0;
    `;
  }

  return `
      border-radius: 0;
  `;
}}

  ${({ noPagerBackground, theme }) => {
    if (!noPagerBackground) {
      return css`
        &:hover {
          background-color: ${theme.submitButtonHover};
        }
      `;
    }
  }}
`;

const BackArrow = styled(Button)`
  border-radius: ${props => props.theme.borderRadius || '0px'} 0 0
    ${props => props.theme.borderRadius || '0px'};
  padding: 6px;
`;

const ForwardArrow = styled(Button)`
  border-radius: 0 ${props => props.theme.borderRadius || '0px'}
    ${props => props.theme.borderRadius || '0px'} 0;
  padding: 6px;
`;

const PageCount = styled.div`
  margin-top: auto;
  margin-bottom: auto;
`;

const Pager = ({
  handlePageArrow, pageCount, noPagerBackground, theme, currentPage, totalRows,
}) => {
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);

  useEffect(() => {
    setRangeTo(pageCount < 5 ? pageCount : 5);
  }, [pageCount]);

  const handlePager = (action) => {
    handlePageArrow(action);

    if (action === 'increment' && rangeTo <= currentPage) {
      setRangeFrom(rangeFrom + 1);
      setRangeTo(rangeTo + 1);
    } else if (action === 'decrement') {
      if (rangeFrom > currentPage - 2) {
        setRangeFrom(rangeFrom - 1);
        setRangeTo(rangeTo - 1);
      }
    } else if (action === 'firstPage') {
      const newRangeTo = pageCount < 5 ? pageCount : 5;
      setRangeFrom(0);
      setRangeTo(newRangeTo);
    } else if (action === 'lastPage') {
      const newRangeFrom = pageCount - 5 > 0 ? pageCount - 5 : 0;
      setRangeFrom(newRangeFrom);
      setRangeTo(pageCount);
    }
  };

  return (
    <Paging>
      <PageCount>
        {totalRows ? (
          <FormattedMessage
            id="Pager.Counter.WithRows"
            defaultMessage="Síða {current} af {total} - {rows} raðir"
            values={{
              current: currentPage,
              total: pageCount,
              rows: totalRows,
            }}
          />
        ) : (
          <FormattedMessage
            id="Pager.Counter"
            defaultMessage="Síða {current} af {total}"
            values={{
              current: currentPage,
              total: pageCount,
            }}
          />
        )}
      </PageCount>
      {pageCount > 1 && 
        <PagerContainer>
          <BackArrow
            noPagerBackground={noPagerBackground}
            isArrow={!noPagerBackground}
            hide={currentPage === 1}
            onClick={() => handlePager('firstPage')}
            id="firstPageArrow"
            type="button"
          >
            <ButtonText role="img" aria-label="arrow-left">
              <DoubleChevronLeft size={16} color={noPagerBackground ? theme.primaryColor : 'white'} />
            </ButtonText>
          </BackArrow>
          <BackArrow
            id="backArrow"
            isArrow={!noPagerBackground}
            noPagerBackground={noPagerBackground}
            hide={currentPage === 1}
            onClick={() => handlePager('decrement')}
            type="button"
          >
            <ButtonText role="img" aria-label="arrow-left">
              <ChevronLeft size={16} color={noPagerBackground ? theme.primaryColor : 'white'} />
            </ButtonText>
          </BackArrow>
          {map(range(rangeFrom, rangeTo), (page, key) => (
            <Button
              onClick={() => handlePager(page + 1)}
              current={page + 1 === currentPage}
              isFirst={currentPage === 1}
              isLast={currentPage === pageCount}
              key={key}
              id={page + 1 === currentPage ? 'currentPageButton' : 'pageButton'}
              noPagerBackground={noPagerBackground}
              type="button"
            >
              <ButtonText current={page + 1 === currentPage}>{page + 1}</ButtonText>
            </Button>
          ))}
          <ForwardArrow
            noPagerBackground={noPagerBackground}
            isArrow={!noPagerBackground}
            hide={currentPage === pageCount}
            onClick={() => handlePager('increment')}
            id="forwardArrow"
            type="button"
          >
            <ButtonText role="img" aria-label="arrow-left">
              <ChevronRight size={16} color={noPagerBackground ? theme.primaryColor : 'white'} />
            </ButtonText>
          </ForwardArrow>
          <ForwardArrow
            noPagerBackground={noPagerBackground}
            isArrow={!noPagerBackground}
            hide={currentPage === pageCount}
            onClick={() => handlePager('lastPage')}
            id="lastPageArrow"
            type="button"
          >
            <ButtonText role="img" aria-label="arrow-left">
              <DoubleChevronRight
                size={16}
                color={noPagerBackground ? theme.primaryColor : 'white'}
              />
            </ButtonText>
          </ForwardArrow>
        </PagerContainer>
      }
    </Paging>
  );
};

Pager.propTypes = {
  handlePageArrow: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  noPagerBackground: PropTypes.bool,
  theme: PropTypes.object,
  totalRows: PropTypes.number,
};

Pager.defaultProps = {
  noPagerBackground: true,
  theme: {},
  totalRows: null,
};

export default withTheme(Pager);
